import { create } from 'zustand';
import { FamilyMember, PersonalInfo, AddRequest, PersonalInfoRequest } from '@/types';
import { api } from '@/lib/api/client';

interface FamilyState {
  members: FamilyMember[];
  personalInfos: PersonalInfo[];
  requests: AddRequest[];
  personalInfoRequests: PersonalInfoRequest[];
  isLoading: boolean;
  error: string | null;
  
  // Member actions
  fetchMembers: () => Promise<void>;
  addMember: (member: Omit<FamilyMember, 'id'>) => Promise<void>;
  updateMember: (id: string, updates: Partial<FamilyMember>) => Promise<void>;
  deleteMember: (id: string) => Promise<void>;
  
  // Request actions
  fetchRequests: () => Promise<void>;
  addRequest: (request: Omit<AddRequest, 'id' | 'status' | 'timestamp'>) => Promise<void>;
  updateRequestStatus: (id: string, status: AddRequest['status']) => Promise<void>;
  
  // Personal info actions
  fetchPersonalInfos: () => Promise<void>;
  addPersonalInfo: (info: Omit<PersonalInfo, 'id'>) => Promise<void>;
  updatePersonalInfo: (id: string, updates: Partial<PersonalInfo>) => Promise<void>;
  deletePersonalInfo: (id: string) => Promise<void>;
}

export const useFamilyStore = create<FamilyState>((set, get) => ({
  members: [],
  personalInfos: [],
  requests: [],
  personalInfoRequests: [],
  isLoading: false,
  error: null,

  // Member actions
  fetchMembers: async () => {
    set({ isLoading: true });
    try {
      const members = await api.get<FamilyMember[]>('/members');
      set({ members });
    } catch (error) {
      set({ error: 'Failed to fetch members' });
    } finally {
      set({ isLoading: false });
    }
  },

  addMember: async (member) => {
    try {
      const newMember = await api.post<FamilyMember>('/members', member);
      set(state => ({ members: [...state.members, newMember] }));
    } catch (error) {
      console.error('Failed to add member:', error);
    }
  },

  updateMember: async (id, updates) => {
    try {
      const updatedMember = await api.patch<FamilyMember>(`/members/${id}`, updates);
      set(state => ({
        members: state.members.map(m => m.id === id ? updatedMember : m)
      }));
    } catch (error) {
      console.error('Failed to update member:', error);
    }
  },

  deleteMember: async (id) => {
    try {
      await api.delete(`/members/${id}`);
      set(state => ({
        members: state.members.filter(m => m.id !== id)
      }));
    } catch (error) {
      console.error('Failed to delete member:', error);
    }
  },

  // Request actions
  fetchRequests: async () => {
    set({ isLoading: true });
    try {
      const [requests, personalInfoRequests] = await Promise.all([
        api.get<AddRequest[]>('/requests'),
        api.get<PersonalInfoRequest[]>('/personal-info-requests')
      ]);
      set({ requests, personalInfoRequests });
    } catch (error) {
      set({ error: 'Failed to fetch requests' });
    } finally {
      set({ isLoading: false });
    }
  },

  addRequest: async (request) => {
    try {
      const newRequest = await api.post<AddRequest>('/requests', request);
      set(state => ({ requests: [...state.requests, newRequest] }));
    } catch (error) {
      console.error('Failed to add request:', error);
    }
  },

  updateRequestStatus: async (id, status) => {
    try {
      const updatedRequest = await api.patch<AddRequest>(`/requests/${id}`, { status });
      set(state => ({
        requests: state.requests.map(r => r.id === id ? updatedRequest : r)
      }));
    } catch (error) {
      console.error('Failed to update request status:', error);
    }
  },

  // Personal info actions
  fetchPersonalInfos: async () => {
    set({ isLoading: true });
    try {
      const personalInfos = await api.get<PersonalInfo[]>('/personal-infos');
      set({ personalInfos });
    } catch (error) {
      set({ error: 'Failed to fetch personal infos' });
    } finally {
      set({ isLoading: false });
    }
  },

  addPersonalInfo: async (info) => {
    try {
      const newInfo = await api.post<PersonalInfo>('/personal-infos', info);
      set(state => ({ personalInfos: [...state.personalInfos, newInfo] }));
    } catch (error) {
      console.error('Failed to add personal info:', error);
    }
  },

  updatePersonalInfo: async (id, updates) => {
    try {
      const updatedInfo = await api.patch<PersonalInfo>(`/personal-infos/${id}`, updates);
      set(state => ({
        personalInfos: state.personalInfos.map(i => i.id === id ? updatedInfo : i)
      }));
    } catch (error) {
      console.error('Failed to update personal info:', error);
    }
  },

  deletePersonalInfo: async (id) => {
    try {
      await api.delete(`/personal-infos/${id}`);
      set(state => ({
        personalInfos: state.personalInfos.filter(i => i.id !== id)
      }));
    } catch (error) {
      console.error('Failed to delete personal info:', error);
    }
  }
}));